import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'
import React from 'react'

import { ReactComponent as ContainerIcon } from 'assets/Container.svg'
import { ReactComponent as UnassignedRouteIcon } from 'assets/UnassignedRouteIcon.svg'
import RouteSequence from 'components/customer-details/content/routing/RouteSequence'
import RouteChip from 'components/customer-details/content/work-orders/common/RouteChip'
import WorkOrderStatusChip from 'components/work-order/WorkOrderStatusChip'
import T from 'T'
import { ALPHA_38_PERCENT } from 'theme/colors'
import { isValidHex } from 'utils/validations'

type Props = {
  workOrderStatus: string
  intentStatus?: string
  woStatusBorderColor?: string
  sequence?: string | number
  serviceName?: string
  addressStr: string
  routeName: string
  routeColor?: string
  routeId?: string
  isUnassigned: boolean
  addressName?: string
  materialType?: string
  materialColor?: string
  measure?: string
  serviceDate?: string
  onAssignStop?: (event: React.MouseEvent) => void
  onSequenceClick?: (event: React.MouseEvent) => void
}

const ServiceItemSummary: React.FC<Props> = ({
  workOrderStatus,
  intentStatus,
  woStatusBorderColor,
  sequence,
  serviceName,
  addressStr,
  routeName,
  routeColor,
  routeId,
  isUnassigned,
  addressName,
  materialType,
  materialColor,
  measure,
  serviceDate,
  onAssignStop,
  onSequenceClick,
}) => {
  const theme = useTheme()
  const hexMaterialColor = materialColor && isValidHex(materialColor) ? materialColor : ''
  const materialBgColor = hexMaterialColor || theme.palette.background.default
  const materialTextColor = hexMaterialColor ? theme.palette.getContrastText(hexMaterialColor) : theme.palette.text.primary

  return (
    <Box sx={{ width: '100%', py: 1, pl: 1, pr: 4 }}>
      <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={1}>
        <Stack alignItems="center" direction="row" minWidth={0} spacing={1.5}>
          <RouteSequence workOrderStatus={workOrderStatus} onClick={isUnassigned ? onAssignStop : onSequenceClick}>
            {sequence || 'U'}
          </RouteSequence>
          <Box minWidth={0}>
            {addressName && (
              <Typography noWrap color="text.secondary" display="block" textTransform="uppercase" variant="caption">
                {addressName}
              </Typography>
            )}
            <Typography noWrap fontWeight={500} variant="body1">
              {addressStr}
            </Typography>
          </Box>
        </Stack>
        <Stack alignItems="center" direction="row" flexShrink={0} spacing={0.5}>
          <WorkOrderStatusChip intentStatus={intentStatus} status={workOrderStatus} />
        </Stack>
      </Stack>

      <Stack alignItems="center" direction="row" justifyContent="space-between" mt={0.75} spacing={1}>
        <Stack alignItems="center" direction="row" flexWrap="nowrap" minWidth={0} overflow="hidden" spacing={1}>
          {routeId && !isUnassigned ? (
            <RouteChip disableRedirect routeColor={routeColor} routeName={routeName} serviceDate={serviceDate} />
          ) : (
            <Chip
              clickable
              icon={
                <SvgIcon sx={{ width: 16, height: 16 }}>
                  <UnassignedRouteIcon />
                </SvgIcon>
              }
              label={`+ ${T.ASSIGN_STOP}`}
              size="small"
              sx={{ borderStyle: 'dashed', borderColor: woStatusBorderColor }}
              variant="outlined"
              onClick={onAssignStop}
            />
          )}
          {materialType && (
            <Chip label={materialType} size="small" sx={{ borderRadius: 1, backgroundColor: materialBgColor, color: materialTextColor }} />
          )}
          {serviceName && (
            <Chip
              avatar={
                <Avatar sx={{ backgroundColor: 'transparent' }}>
                  <CalendarTodayOutlinedIcon sx={{ width: 14, height: 14, color: 'text.secondary' }} />
                </Avatar>
              }
              label={serviceName}
              size="small"
              sx={{ border: 'none' }}
              variant="outlined"
            />
          )}
        </Stack>
        <Box flexShrink={0}>
          <Chip
            avatar={
              <Avatar sx={{ backgroundColor: `${theme.palette.background.black}${ALPHA_38_PERCENT}` }}>
                <SvgIcon inheritViewBox sx={{ width: 12, height: 12, color: theme.palette.background.paper }}>
                  <ContainerIcon />
                </SvgIcon>
              </Avatar>
            }
            label={measure}
            size="small"
            variant="outlined"
          />
        </Box>
      </Stack>
    </Box>
  )
}

export default ServiceItemSummary
